import { FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Arrow } from '$icons/uncolored/ArrowWithTail.svg';
import SPKCandidateCardSkill from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateSkills';
import SPKCandidateHiddenSkills from '$components/SPKCandidateList/SPKCandidateCard/SPKCandidateHiddenSkills';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import PositionDto from '../../types/serverTypes/positionDto';
import classes from './PositionPage.module.css';
import EditJobModal from '../AddNewJobModal/EditJobModal';
import PositionCardMenu from './PositionCardMenu/PositionCardMenu';
import DuplicatePositionModal from '../DuplicateJobModal/DuplicatePositionModal';
import { SaveState } from '../../types/ForrmType';

interface Props {
  position: PositionInternalDto | PositionDto;
}

const PositionCard: FC<Props> = ({ position }) => {
  const [t] = useTranslation('global');
  const [openModal, setOpenModal] = useState(false);

  const [openDuplicateModal, setDuplicateModal] = useState(
    position.actionStatus === SaveState.IDLE
  );
  const card = useRef<HTMLDivElement>(null);

  const handleOpenDuplicateModal = (show: boolean) => setDuplicateModal(show);

  const [overflowStart, setOverflowStart] = useState<number>();
  const [hiddenSkills, setHiddenSkills] = useState<
    PositionDto['Skills_position']
  >([]);
  const [visibleSkills, setVisibleSkills] = useState<
    PositionDto['Skills_position']
  >([]);

  const skillsContainerRef = useRef<HTMLDivElement | null>(null);

  const onOverflowHandler = (index: number) => {
    setOverflowStart(index);
  };

  useEffect(() => {
    if (!position.Skills_position) return;

    if (!overflowStart && position.Skills_position) {
      setVisibleSkills(position.Skills_position);
      return;
    }

    const visible = position.Skills_position.slice(0, overflowStart);
    const hidden = position.Skills_position.slice(overflowStart);

    setVisibleSkills(visible);
    setHiddenSkills(hidden);
  }, [position.Skills_position, overflowStart]);

  return (
    <>
      <div>
        <div
          ref={card}
          // className={classes.card}
          className="rounded-t-xl bg-white p-4"
          data-testid="positionCard"
          tabIndex={-1}
        >
          <div className={`mb-4 ${classes.cardHeader}`}>
            <div className={classes.cardJob}>
              <p className="text-xl font-bold">{position.JobPosition.title}</p>
            </div>
            <PositionCardMenu
              isInternal={false}
              setOpenEditModal={setOpenModal}
              position={position}
              handleOpenDuplicateModal={handleOpenDuplicateModal}
            />
          </div>
          <div className="flex flex-row divide-x -mx-2 *:px-2 text-sm">
            <div className="flex flex-row gap-2">
              <span className="text-spk-mediumContrast">{`${t(
                'PositionCard.Experience'
              )}`}</span>
              <span>{`${position.ExperienceYears} years`}</span>
            </div>

            <div className="flex flex-row gap-2">
              <span className="text-spk-mediumContrast">
                {t('PositionCard.USDMonthlyBudget')}:
              </span>
              <span>{`$${position.SalaryLowerBound} - $${position.SalaryUpperBound}`}</span>
            </div>
          </div>

          {/* Skills */}
          <div className="items-center text-sm flex flex-row gap-2 mb-4 mt-2 text-nowrap text-spk-mediumContrast">
            <p>{`${t('PositionCard.SkillsetOverview')}:`}</p>
            <div className="flex flex-row gap-2 overflow-hidden">
              {visibleSkills &&
                visibleSkills
                  .slice(0, overflowStart)
                  .map((singleSkill, index) => {
                    return (
                      <SPKCandidateCardSkill
                        key={`${singleSkill.Title}_${singleSkill.Id}`}
                        skill={singleSkill.Title}
                        index={index}
                        containerRef={
                          skillsContainerRef.current as HTMLDivElement
                        }
                        setOverflowIndex={onOverflowHandler}
                      />
                    );
                  })}
            </div>

            {hiddenSkills && hiddenSkills.length > 0 && (
              <SPKCandidateHiddenSkills
                hiddenSkills={hiddenSkills.map((singleSkill) => ({
                  Id: singleSkill.Id,
                  Skill: { title: singleSkill.Title },
                }))}
                uniqueId={position.Id}
              />
            )}
          </div>
        </div>
        <Link
          data-testid="viewCandidatesButton"
          role="button"
          tabIndex={0}
          className="text-sm font-bold text-spk-brightBlue flex flex-row justify-end items-center gap-2 bg-spk-neutral-10 rounded-b-xl p-4"
          onKeyDown={() => {}}
          to={`/employer-dashboard/positions/${position.Id}`}
        >
          {`${
            position.CandidatesPositionStatus?.filter(
              (singleCandidate) => singleCandidate.Status !== 'declined'
            )?.length || 0
          } ${t('PositionCard.candidates')}`}
          <Arrow className="fill-spk-brightBlue" />
        </Link>
      </div>
      <EditJobModal
        modalIsOpen={openModal}
        setModal={setOpenModal}
        position={position}
      />
      <DuplicatePositionModal
        modalIsOpen={openDuplicateModal}
        setModal={handleOpenDuplicateModal}
        position={position}
      />
    </>
  );
};

export default PositionCard;
